import {IngestionMessage, MessageScope} from "../asset-service-api";
import {useProjects} from "../Behaviors/projects";
import { useApp } from "../Behaviors/app";
import { useStore } from "../State/zustandStore";
import { ProjectType } from "../api";

export const useSdValidator = (projId?: string | undefined) => {
    const workspaceState = useStore().projects;
    const projectId = workspaceState.selectedProjectId ?? projId;
    const {project, getMappedTrack, getMappedResources} = useProjects(projectId!);
    const app = useApp();

    const isProductSd = () => {
        switch (project?.releaseInfo?.configuration) {
        case "Mastertone/Realtone/Ringback":
        case "Mastertone/Realtone (Speechtone + Mastertone)":
        case "eAlbum Audio":
        case "eAlbum Audio + Booklet":
        case "eFixedPlaylist Audio":
        case "eSingle Audio/Multi Track":
        case "eSingle Audio/Multi Track + Booklet":
        case "eSingle Audio/Single Track":
        case "eSingle Audio/Single Track + Booklet":
        case "eSnippet Audio":
        case "eMix Bundle Album":
        case "eMix Bundle Album + Booklet":
        case "eMix Bundle Album + LP":
        case "eMix Bundle Single":
        case "eMix Bundle Single + Booklet":
        case "eAlbum Audio (Sollos)":
        case "eSingle Audio/Single Track (Sollos)":
        case "eSingle Audio/Multi Track (Sollos)":
            return true;
        default:
            return false;
        }
    };

    const getMessage = (enableHdForSd: boolean) => {
        if (project?.projectType === ProjectType.Release) {
            return `Tracks on SD products must be ${enableHdForSd ? "16, 24, or 32" : "16"} bit.`;
        }
        else if (project?.projectType !== ProjectType.Resource) {
            return `Asset assigned to audio resource must be ${enableHdForSd ? "16, 24, or 32" : "16"} bit.`;
        } //We are allowing assets with all bit depth for WAV-ISRCs/Resource projects
    };

    const getScope = () => {
        return project?.projectType === ProjectType.Resource ? MessageScope.Resource : MessageScope.Track;
    };

    const getSDValidationErrors = (): IngestionMessage[] => {
        const {enableHdForSd} = app.features;
        const allowedBitDepths = enableHdForSd ? [16, 24, 32] : [16];
        const allowedChannels = 2;

        if (isProductSd() && project?.projectType !== ProjectType.Resource) {
            const errorMessages = getMappedResources().filter(x => !allowedBitDepths.includes(x.audioInfo?.bit_depth ?? 0)).map(x => {
                return {
                    details: `Asset ${ x.filename }, assigned to Track ${ getMappedTrack(project!, x)?.number }, Bit Depth (${ x.audioInfo?.bit_depth }) must be (${enableHdForSd ? "16, 24, or 32" : "16"}) for configuration ${project?.releaseInfo?.configuration}`,
                    message: getMessage(enableHdForSd),
                    messageType: "Error",
                    source: {
                        id: "6C6E5CAE-638D-406C-9FDB-CD665BC6AE4C",
                        scope: getScope(),
                        stage: "local",
                        target: getMappedTrack(project!, x)?.number.toString(),
                    },
                } as IngestionMessage;
            });

            errorMessages.push(...getMappedResources().filter(x => (x.audioInfo?.channels ?? 0) !== allowedChannels).map(x => {
                return {
                    message: `File has ${x.audioInfo?.channels ?? 0} audio channels. This product configuration requires ${allowedChannels} channels.`,
                    messageType: "Error",
                    source: {
                        scope: getScope(),
                        stage: "local",
                        target: getMappedTrack(project!, x)?.number.toString(),
                        id: "05377e7b-8936-4112-b45d-e362c46d2ff2",
                    },
                } as IngestionMessage;
            }));
            return errorMessages;
        } else return [];
    };
    return { getSDValidationErrors, isProductSd};
};
